import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { configurationApi } from '../api/configuration.api';
import { useConfirm } from '../../../shared/components/ConfirmDialog/useConfirm';

type TypeCalcul = 'MONTANT_FIXE' | 'POURCENTAGE_BASE' | 'POURCENTAGE_BRUT' | 'FORMULE_CUSTOM' | 'CONDITIONNEL';

interface PrimeTemplate {
  idPrimeTemplate: string;
  code: string;
  nom: string;
  description?: string;
  typeCalcul: TypeCalcul;
  montantSuggere?: number | null;
  tauxSuggere?: number | null;
  plafondSuggere?: number | null;
  ordreBulletin: number;
  afficherBulletin: boolean;
  actif: boolean;
}

const TYPE_LABELS: Record<TypeCalcul, string> = {
  MONTANT_FIXE: 'Montant fixe',
  POURCENTAGE_BASE: '% base',
  POURCENTAGE_BRUT: '% brut',
  FORMULE_CUSTOM: 'Formule',
  CONDITIONNEL: 'Conditionnel',
};

const TYPE_COLORS: Record<TypeCalcul, string> = {
  MONTANT_FIXE: 'bg-primary-light text-primary-text',
  POURCENTAGE_BASE: 'bg-violet-100 text-violet-700',
  POURCENTAGE_BRUT: 'bg-accent-light text-accent',
  FORMULE_CUSTOM: 'bg-warning-light text-warning',
  CONDITIONNEL: 'bg-danger-light text-danger',
};

const FORM_VIDE = {
  code: '',
  nom: '',
  description: '',
  typeCalcul: 'MONTANT_FIXE' as TypeCalcul,
  montantSuggere: '',
  tauxSuggere: '',
  plafondSuggere: '',
  ordreBulletin: 0,
  afficherBulletin: true,
};

const f = (n: number) => n.toLocaleString('fr-FR') + ' F';
const fPct = (n: number) => (n * 100).toFixed(2) + '%';

export function CataloguePrimesPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(FORM_VIDE);
  const [editId, setEditId] = useState<string | null>(null);

  const { data: templates = [], isLoading } = useQuery<PrimeTemplate[]>({
    queryKey: ['prime-templates'],
    queryFn: configurationApi.listerTemplatesPrimes,
  });

  const invalider = () => qc.invalidateQueries({ queryKey: ['prime-templates'] });

  const creer = useMutation({
    mutationFn: (data: any) => configurationApi.creerTemplatePrime(data),
    onSuccess: () => { invalider(); resetForm(); },
  });
  const modifier = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => configurationApi.modifierTemplatePrime(id, data),
    onSuccess: () => { invalider(); resetForm(); },
  });
  const desactiver = useMutation({
    mutationFn: (id: string) => configurationApi.desactiverTemplatePrime(id),
    onSuccess: invalider,
  });

  const resetForm = () => {
    setForm(FORM_VIDE);
    setShowForm(false);
    setEditId(null);
  };

  const ouvrirEdition = (t: PrimeTemplate) => {
    setForm({
      code: t.code,
      nom: t.nom,
      description: t.description ?? '',
      typeCalcul: t.typeCalcul,
      montantSuggere: t.montantSuggere != null ? String(t.montantSuggere) : '',
      tauxSuggere: t.tauxSuggere != null ? String(t.tauxSuggere) : '',
      plafondSuggere: t.plafondSuggere != null ? String(t.plafondSuggere) : '',
      ordreBulletin: t.ordreBulletin,
      afficherBulletin: t.afficherBulletin,
    });
    setEditId(t.idPrimeTemplate);
    setShowForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      code: form.code,
      nom: form.nom,
      description: form.description || undefined,
      typeCalcul: form.typeCalcul,
      montantSuggere: form.montantSuggere ? Number(form.montantSuggere) : undefined,
      tauxSuggere: form.tauxSuggere ? Number(form.tauxSuggere) : undefined,
      plafondSuggere: form.plafondSuggere ? Number(form.plafondSuggere) : undefined,
      ordreBulletin: form.ordreBulletin,
      afficherBulletin: form.afficherBulletin,
    };
    if (editId) {
      modifier.mutate({ id: editId, data: payload });
    } else {
      creer.mutate(payload);
    }
  };

  const afficheValeur = (t: PrimeTemplate) => {
    if (t.typeCalcul === 'MONTANT_FIXE' && t.montantSuggere != null) return f(Number(t.montantSuggere));
    if ((t.typeCalcul === 'POURCENTAGE_BASE' || t.typeCalcul === 'POURCENTAGE_BRUT') && t.tauxSuggere != null)
      return fPct(Number(t.tauxSuggere));
    return '—';
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-content">Catalogue des primes</h1>
          <p className="text-sm text-content-subtle mt-1">Modèles de primes et indemnités disponibles</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(v => !v); }}
          className="px-4 py-2 bg-primary text-content-inverted rounded-lg text-sm font-medium hover:bg-primary-hover transition-colors"
        >
          {showForm && !editId ? 'Annuler' : '+ Nouveau modèle'}
        </button>
      </div>

      {/* Formulaire */}
      {showForm && (
        <div className="bg-surface border border-border rounded-xl p-6 shadow-sm">
          <h2 className="text-base font-semibold text-content mb-4">
            {editId ? 'Modifier le modèle' : 'Nouveau modèle de prime'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-content-muted mb-1">Code *</label>
                <input
                  required
                  value={form.code}
                  onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                  placeholder="PRIME_TRANSPORT"
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-content-muted mb-1">Nom *</label>
                <input
                  required
                  value={form.nom}
                  onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                  placeholder="Prime de transport"
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-content-muted mb-1">Type de calcul *</label>
                <select
                  value={form.typeCalcul}
                  onChange={e => setForm(f => ({ ...f, typeCalcul: e.target.value as TypeCalcul }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                >
                  {Object.entries(TYPE_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-content-muted mb-1">Description</label>
                <input
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {(form.typeCalcul === 'MONTANT_FIXE' || form.typeCalcul === 'FORMULE_CUSTOM' || form.typeCalcul === 'CONDITIONNEL') && (
                <div>
                  <label className="block text-xs font-medium text-content-muted mb-1">Montant suggéré (F)</label>
                  <input
                    type="number"
                    value={form.montantSuggere}
                    onChange={e => setForm(f => ({ ...f, montantSuggere: e.target.value }))}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                  />
                </div>
              )}
              {(form.typeCalcul === 'POURCENTAGE_BASE' || form.typeCalcul === 'POURCENTAGE_BRUT') && (
                <div>
                  <label className="block text-xs font-medium text-content-muted mb-1">Taux suggéré (ex: 0.05 = 5%)</label>
                  <input
                    type="number"
                    step="0.001"
                    value={form.tauxSuggere}
                    onChange={e => setForm(f => ({ ...f, tauxSuggere: e.target.value }))}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                  />
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-content-muted mb-1">Plafond (F, optionnel)</label>
                <input
                  type="number"
                  value={form.plafondSuggere}
                  onChange={e => setForm(f => ({ ...f, plafondSuggere: e.target.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-content-muted mb-1">Ordre bulletin</label>
                <input
                  type="number"
                  value={form.ordreBulletin}
                  onChange={e => setForm(f => ({ ...f, ordreBulletin: Number(e.target.value) }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="afficher"
                checked={form.afficherBulletin}
                onChange={e => setForm(f => ({ ...f, afficherBulletin: e.target.checked }))}
                className="rounded"
              />
              <label htmlFor="afficher" className="text-sm text-content-muted">Afficher sur le bulletin</label>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={creer.isPending || modifier.isPending}
                className="px-5 py-2 bg-primary text-content-inverted rounded-lg text-sm font-medium hover:bg-primary-hover disabled:opacity-50 transition-colors"
              >
                {creer.isPending || modifier.isPending ? 'Enregistrement...' : editId ? 'Modifier' : 'Créer'}
              </button>
              <button type="button" onClick={resetForm} className="px-5 py-2 border border-border text-content-muted rounded-lg text-sm font-medium hover:bg-surface-raised transition-colors">
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Liste */}
      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        {isLoading && <p className="p-6 text-sm text-content-subtle">Chargement...</p>}
        {!isLoading && templates.length === 0 && (
          <p className="p-8 text-center text-sm text-content-subtle">Aucun modèle de prime. Créez le premier.</p>
        )}
        {templates.length > 0 && (
          <table className="w-full">
            <thead className="bg-surface-raised border-b border-border">
              <tr>
                {['Code', 'Nom', 'Type', 'Valeur suggérée', 'Plafond', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-content-subtle uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {templates.map(t => (
                <tr key={t.idPrimeTemplate} className="hover:bg-surface-raised transition-colors">
                  <td className="px-4 py-3 text-sm font-mono text-content-subtle">{t.code}</td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-content">{t.nom}</p>
                    {t.description && <p className="text-xs text-content-subtle mt-0.5">{t.description}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[t.typeCalcul]}`}>
                      {TYPE_LABELS[t.typeCalcul]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-content-muted">{afficheValeur(t)}</td>
                  <td className="px-4 py-3 text-sm text-content-subtle">
                    {t.plafondSuggere != null ? f(Number(t.plafondSuggere)) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => ouvrirEdition(t)}
                        className="text-xs text-primary-text hover:text-primary font-medium"
                      >
                        Modifier
                      </button>
                      <button
                        onClick={async () => {
                          const ok = await confirm({
                            title: `Désactiver "${t.nom}" ?`,
                            message: 'Ce modèle de prime ne sera plus proposé lors de l\'attribution.',
                            variant: 'warning',
                            labelConfirm: 'Désactiver',
                          });
                          if (ok) desactiver.mutate(t.idPrimeTemplate);
                        }}
                        disabled={desactiver.isPending}
                        className="text-xs text-danger hover:text-danger font-medium disabled:opacity-50"
                      >
                        Désactiver
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
